/**
 * Per-fulfillment shipping option control: a select-style trigger that expands the carrier table
 * inline. Picking an option collapses the panel again, mirroring a dropdown.
 */
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import Currency from '../../../components/currency/currency';
import FontBody2 from '../../../components/fonts/font-body2/font-body2';
import { FONT_COLOR, FONT_VARIANT } from '../../../components/fonts/font-props';
import IconDropDownMediumArrow from '../../../components/icons/icon-drop-down-medium-arrow/icon-drop-down-medium-arrow';
import IconPackage from '../../../components/icons/icon-package/icon-package';
import Icon from '../../../components/icons/icon/icon';
import useDashboardTranslation from '../../../dashboard-translation/hooks/dashboard-translation-hook';
import CreateRmaDynamicShippingOptionsStore from '../stores/create-rma-dynamic-shipping-store';
import ShippingOptionsSection from './shipping-options-section';
import './shipping-option-picker.scss';

const PAGE_SCOPE = 'dashboard-create-rma';

export interface IShippingOptionPickerProps {
    isMixedOrder: boolean;
    index: number;
    dynamicShippingOptionsStore: CreateRmaDynamicShippingOptionsStore;
    /** the fulfillment's RMA already exists, so its carrier can no longer be changed (FR13) */
    isDisabled?: boolean;
}

const ShippingOptionPicker: React.FC<IShippingOptionPickerProps> = observer(({
    isMixedOrder,
    index,
    dynamicShippingOptionsStore,
    isDisabled = false,
}) => {
    const { getTranslation } = useDashboardTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const selectedOption = dynamicShippingOptionsStore.selectedOption;
    const isInvalid = !isDisabled && dynamicShippingOptionsStore.shouldSelectShippingOption;
    const isPanelOpen = isOpen && !isDisabled;

    const triggerClassNames = [
        'shipping-option-picker__trigger',
        isPanelOpen ? 'is-open' : '',
        isInvalid ? 'is-invalid' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className="shipping-option-picker" data-testid="shipping-option-picker">
            <div className="shipping-option-picker__label">
                {isMixedOrder &&
                    <>
                        <Icon color={'var(--color-primary-2)'} Component={IconPackage} />
                        <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                            {getTranslation('FULFILLMENT_SECTION_TITLE', PAGE_SCOPE, [String(index)])}
                        </FontBody2>
                        <span className="shipping-option-picker__label-divider" />
                    </>
                }
                <FontBody2 color={FONT_COLOR.PRIMARY3}>
                    {getTranslation('SHIPPING_OPTION', PAGE_SCOPE)}
                </FontBody2>
                <FontBody2 color={FONT_COLOR.ACCENT1}>*</FontBody2>
            </div>
            <button
                type="button"
                className={triggerClassNames}
                aria-expanded={isPanelOpen}
                disabled={isDisabled}
                onClick={() => setIsOpen(!isOpen)}
            >
                {selectedOption
                    ? <span className="shipping-option-picker__value">
                        <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{selectedOption.name}</FontBody2>
                        <FontBody2 color={FONT_COLOR.NEUTRAL3}>{selectedOption.description}</FontBody2>
                        <FontBody2 color={FONT_COLOR.PRIMARY3}>
                            <Currency currencyCode={selectedOption.currency_code} amount={selectedOption.cost} useNative />
                        </FontBody2>
                      </span>
                    : <FontBody2 color={FONT_COLOR.PRIMARY3_50}>
                        {getTranslation('SELECT_A_SHIPPING_OPTION', PAGE_SCOPE)}
                      </FontBody2>
                }
                <span className="shipping-option-picker__chevron">
                    <IconDropDownMediumArrow color={'var(--color-accent-1)'} />
                </span>
            </button>
            {isPanelOpen &&
                <div className="shipping-option-picker__panel">
                    <ShippingOptionsSection
                        dynamicShippingOptionsStore={dynamicShippingOptionsStore}
                        radioGroupName={`create-rma-dynamic-shipping-option-${index}`}
                        onOptionSelected={() => setIsOpen(false)}
                    />
                </div>
            }
        </div>
    );
});

export default ShippingOptionPicker;
